import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getEmployeeOptions,
  getEmployeeTrendOptions,
  getWorkforceSummaryOptions,
  listEmployeeFindingsOptions,
  listEmployeePermissionGrantsOptions,
  listEmployeeReflectionsOptions,
  listEmployeeRunsOptions,
  listEmployeesOptions,
  listWorkforceAssignmentsOptions,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { WorkforceAssignment } from '../../generated/api/types.gen.js';
import { type Assignment, toEmployeeView, type EmployeeView } from '../../pages/workforce/shared';

export function useEmployees() {
  const query = useQuery(listEmployeesOptions());
  const employees: EmployeeView[] = useMemo(
    () => (query.data?.data ?? []).map((row) => toEmployeeView(row)),
    [query.data],
  );
  if (query.isError) console.error('useEmployees: failed to load workforce employees', query.error);
  return { ...query, employees };
}

export function useEmployee(id: string | undefined) {
  return useQuery({
    ...getEmployeeOptions({ path: { id: id ?? '' } }),
    enabled: !!id,
    select: (record): EmployeeView => toEmployeeView(record),
  });
}

export function useWorkforceSummary() {
  return useQuery(getWorkforceSummaryOptions());
}

export function useEmployeeRuns(id: string | undefined) {
  return useQuery({
    ...listEmployeeRunsOptions({ path: { id: id ?? '' } }),
    enabled: !!id,
    select: (list): Record<string, unknown>[] => list.data,
  });
}

export function useEmployeeReflections(id: string | undefined) {
  return useQuery({
    ...listEmployeeReflectionsOptions({ path: { id: id ?? '' } }),
    enabled: !!id,
    select: (list): Record<string, unknown>[] => list.data,
  });
}

export function useEmployeeFindings(id: string | undefined) {
  return useQuery({
    ...listEmployeeFindingsOptions({ path: { id: id ?? '' } }),
    enabled: !!id,
    select: (list): Record<string, unknown>[] => list.data,
  });
}

export function useEmployeePermissionGrants(id: string | undefined) {
  return useQuery({
    ...listEmployeePermissionGrantsOptions({ path: { id: id ?? '' } }),
    enabled: !!id,
    select: (list): Record<string, unknown>[] => list.data,
  });
}

export function useEmployeeTrend(id: string | undefined) {
  return useQuery({
    ...getEmployeeTrendOptions({ path: { id: id ?? '' } }),
    enabled: !!id,
  });
}

function toAssignment(row: WorkforceAssignment): Assignment {
  return {
    projectId: row.projectId,
    projectName: row.projectName,
    active: row.active,
    team: row.teamName,
    since: row.since,
    endedAt: row.endedAt,
  };
}

/**
 * Org-wide workforce-member project assignments, grouped by employee id. Backed
 * by the real `team_members(workforce_member) → teams → projects` feed
 * (`GET /workforce/assignments`). Currently empty until workforce-member team
 * memberships exist — the map will simply have no entries.
 */
export function useWorkforceAssignments() {
  const query = useQuery({
    ...listWorkforceAssignmentsOptions(),
    select: (list): WorkforceAssignment[] => list.data,
  });
  const byEmployee: Map<string, Assignment[]> = useMemo(() => {
    const map = new Map<string, Assignment[]>();
    for (const row of query.data ?? []) {
      const list = map.get(row.employeeId) ?? [];
      list.push(toAssignment(row));
      map.set(row.employeeId, list);
    }
    return map;
  }, [query.data]);
  return { ...query, byEmployee };
}
